import React from 'react'
import { css } from '@emotion/css'
import { theme } from '@contrast-security-inc/design-system-foundations'
import { Text, Link } from '@contrast-security-inc/design-system-typography'
import { range } from 'lodash'
import {
  convertFacetsToFilters,
  facetNameToApplicationFilterNameMap
} from '../../../header/filters'
import { SelectedValueReportingFacet } from '../../../../../graphql/schema/types/value-reporting'
import {
  LoadingMaskRectangle,
  LoadingMaskSeverityBadge,
  LoadingMaskThermometer
} from '../../../../../legacy-design-system/loading-masks'
import { NameValueType } from './type/vr-histogram-view-rule-type'
import { VulnerabilityStatusRegistry } from '../../../../../graphql/schema/shared'
import { VulnerabilityStatus } from '../../../../shared'
import { useApplicationSecuritySummaryFacets } from '../../../api/use-application-security-summary-facets'

const cssMain = css`
  display: flex;
  align-items: stretch;
  > * {
    display: flex;
    flex-direction: column;
    justify-content: space-around;
  }
`
const cssBadges = css`
  flex-grow: 0;
  margin-right: 10px;
  > * {
    margin: 6px 0;
  }
`
const cssThermometers = css`
  flex-grow: 1;
  margin-right: 10px;
`
const cssCounts = css`
  flex-grow: 0;
`

const cssThermometerLayout = css`
  display: flex;
  flex-direction: column;
`
const cssThermometerRow = (count) => css`
  display: flex;
  margin: 6px 0;
  cursor: ${count > 0 ? 'pointer' : 'default'};
`
const widthByType = {
  SEVERITY: '72px',
  STATUS: '112px',
  TYPE: '160px'
}
const cssThermometerBadge = (type) => css`
  width: ${widthByType[type] || '112px'};
`
const cssThermometerRatio = css`
  flex: 1;
  display: flex;
  align-items: center;
  padding: 0 10px 0 10px;
`
const cssThermometerCount = css`
  color: ${theme.color.neutral60};
  display: flex;
  align-items: center;
  flex: 0 1 6%;
`

export interface HistogramViewItem {
  /**
   * The view item string or key
   */
  readonly item: string | NameValueType
  /**
   * The count associated to the item
   */
  readonly count: number
  /**
   * The thermometer fill ratio (0 - 1.0)
   */
  readonly ratio: number
  /**
   * The value of the filter associated with the type
   */
  readonly value?: string
}

interface Data {
  /**
   * The list of items to be displayed via renderLabel()
   */
  readonly items: string[] | NameValueType[]
  /**
   * The list of counts associated to each respective item
   */
  readonly counts: number[]
}

interface Props {
  readonly organizationUuid: string
  readonly selectedFacets: SelectedValueReportingFacet[]
  readonly histogramType: string

  readonly data: Data

  /**
   * Processes a set of raw data and converts a list of sorted view items
   */
  readonly processSummary: (
    items: string[] | NameValueType[],
    counts: number[],
    ratios: number[]
  ) => Array<HistogramViewItem>
  /**
   * Render logic for the label (or badge) component of the view
   */
  readonly renderLabel: (
    data: HistogramViewItem,
    index?: number
  ) => React.ReactNode
  /**
   * Render logic for the thermometer component of the view
   */
  readonly renderThermometer: (data: HistogramViewItem) => React.ReactNode

  readonly e2eTestId?: string
}

/**
 * Generic histogram view responsible for the core layout and
 * view state management (loading, error, success)
 */
export function VrHistogramView({
  organizationUuid,
  selectedFacets,
  histogramType,
  data,
  processSummary,
  renderLabel,
  renderThermometer,
  e2eTestId
}: Props) {
  return (
    <VrHistogramViewLoaded
      organizationUuid={organizationUuid}
      selectedFacets={selectedFacets}
      histogramType={histogramType}
      e2eTestId={e2eTestId}
      data={data}
      processSummary={processSummary}
      renderLabel={renderLabel}
      renderThermometer={renderThermometer}
    />
  )
}

function VrHistogramViewLayout({
  renderBadges,
  renderThermometers,
  renderCounts,
  e2eTestId
}: {
  readonly renderBadges: JSX.Element
  readonly renderThermometers: JSX.Element
  readonly renderCounts: JSX.Element
  readonly e2eTestId?: string
}) {
  /**
   * This component uses 3 columns so that the count width is equal on all rows
   * as depending on how many digits the count component is,
   * it can affect the layout without this approach.
   */
  return (
    <div className={cssMain} data-e2e={e2eTestId}>
      <div className={cssBadges}>{renderBadges}</div>
      <div className={cssThermometers}>{renderThermometers}</div>
      <div className={cssCounts}>{renderCounts}</div>
    </div>
  )
}

export function VrHistogramViewLoading({ total }: { readonly total: number }) {
  const mapper = (View: () => JSX.Element) =>
    range(0, total).map((value) => <View key={value} />)

  return (
    <VrHistogramViewLayout
      renderBadges={
        <>
          {mapper(() => (
            <LoadingMaskSeverityBadge />
          ))}
        </>
      }
      renderThermometers={
        <>
          {mapper(() => (
            <LoadingMaskThermometer />
          ))}
        </>
      }
      renderCounts={
        <>
          {mapper(() => (
            <LoadingMaskRectangle width={25} height={15} />
          ))}
        </>
      }
    />
  )
}

const filterKeyByType = {
  SEVERITY: 'severities',
  STATUS: 'statuses',
  TYPE: 'types'
}
export function VrHistogramViewLoaded({
  organizationUuid,
  selectedFacets,
  histogramType,
  data: { items, counts },
  processSummary,
  renderLabel,
  renderThermometer,
  e2eTestId
}: Props) {
  const max = Math.max(...counts)
  const ratios = counts.map((c) => (max === 0 ? 0 : c / max))
  const summary = processSummary(items, counts, ratios)

  const {
    data: { applicationSecuritySummaryFacetsRequest }
  } = useApplicationSecuritySummaryFacets({
    organizationUuid,
    selectedFacets
  })

  const additionalFilters = convertFacetsToFilters(
    facetNameToApplicationFilterNameMap,
    selectedFacets,
    applicationSecuritySummaryFacetsRequest
  )

  return (
    <div
      className={cssThermometerLayout}
      data-testid={e2eTestId}
      data-e2e={e2eTestId}
    >
      {summary.map((it, index) => {
        const { item, count } = it
        const countTestId = `${e2eTestId}-count-${index}-${count}`
        const params = `${filterKeyByType[histogramType]}=${
          (item as NameValueType).value || item
        }`
        const vulnStatusAddendum = Object.values(
          VulnerabilityStatusRegistry
        ).includes(item as VulnerabilityStatus)
          ? '&quickFilter=ALL'
          : ''
        const href = `#/${organizationUuid}/vulns?licensedOnly=true&${params}${additionalFilters}${vulnStatusAddendum}`
        const showLinks = count > 0
        const WrapperComponent = showLinks ? Link : React.Fragment
        const wrapperProps = showLinks
          ? { style: { textDecoration: 'none', display: 'inline' }, href }
          : {}

        return (
          <WrapperComponent
            {...wrapperProps}
            key={
              typeof item === 'string' ? item : (item as NameValueType).value
            }
          >
            <div className={cssThermometerRow(count)}>
              <div className={cssThermometerBadge(histogramType)}>
                {renderLabel(it, index)}
              </div>
              <div className={cssThermometerRatio}>{renderThermometer(it)}</div>
              <div className={cssThermometerCount}>
                <Text
                  font="labelSm"
                  data-testid={countTestId}
                  data-e2e={countTestId}
                >
                  ({count})
                </Text>
              </div>
            </div>
          </WrapperComponent>
        )
      })}
    </div>
  )
}

/**
 * Finds a key and converts to a view item out of a set of raw histogram data.
 */
export function processHistogramViewItem(
  key: string | NameValueType,
  items: string[] | NameValueType[],
  counts: number[],
  ratios: number[]
): HistogramViewItem {
  const index = items.findIndex(
    (item) => item === key || item.displayName === key
  )

  return index < 0
    ? { item: key, ratio: 0, count: 0 }
    : {
        item: key,
        ratio: ratios[index],
        count: counts[index]
      }
}
